import { z } from "zod";
import { pageOf, pageQuerySchema } from "../common/pagination.js";
import { patchOf } from "../common/patch.js";
import { quantitySchema, timestampSchema, unitPriceSchema, uuidSchema } from "../common/primitives.js";
import { positiveQuantitySchema, warehouseKindSchema } from "./enums.js";
/**
 * Market kataloğu (Prompt 16): ürün – tedarikçi bağları, istasyon × ürün ayarı ve depolar.
 * Ürünün kendi alanları (marka, SKT takibi, koli, net içerik) katalog modülündedir (`/products`).
 */
// ── Ürün – tedarikçi ─────────────────────────────────────────────────────────
export const productSupplierSchema = z.object({
    id: uuidSchema,
    productId: uuidSchema,
    productCode: z.string(),
    productName: z.string(),
    counterpartyId: uuidSchema,
    counterpartyName: z.string(),
    supplierProductCode: z.string().nullable(),
    purchasePrice: unitPriceSchema.nullable().describe("Liste alış fiyatı, KDV HARİÇ, satış birimi başına (₺)"),
    packSize: z.string().nullable().describe("Tedarikçinin koli içi adedi (boşsa ürününki)"),
    leadTimeDays: z.number().int(),
    minOrderQuantity: z.string().nullable(),
    isPreferred: z.boolean().describe("Sipariş önerisinin kullandığı tedarikçi (ürün başına en çok bir)"),
    isActive: z.boolean(),
    version: z.number().int(),
    updatedAt: timestampSchema,
});
export const productSupplierPageSchema = pageOf(productSupplierSchema);
export const listProductSuppliersQuerySchema = pageQuerySchema.extend({
    productId: uuidSchema.optional(),
    counterpartyId: uuidSchema.optional(),
    includeInactive: z.enum(["true", "false"]).optional(),
});
const nonNegativePrice = unitPriceSchema.refine((value) => Number(value) >= 0, "Fiyat negatif olamaz");
export const createProductSupplierSchema = z.object({
    productId: uuidSchema,
    counterpartyId: uuidSchema.describe("Tedarikçi carisi (etkin; rolü tedarikçi ya da dağıtıcı)"),
    supplierProductCode: z.string().trim().min(1).max(60).nullable().default(null),
    purchasePrice: nonNegativePrice.nullable().default(null),
    packSize: positiveQuantitySchema.nullable().default(null),
    leadTimeDays: z.number().int().min(0).max(365).default(2),
    minOrderQuantity: positiveQuantitySchema.nullable().default(null),
    isPreferred: z.boolean().default(false),
});
export const updateProductSupplierSchema = patchOf(createProductSupplierSchema.omit({ productId: true, counterpartyId: true })).extend({
    isActive: z.boolean().optional(),
    version: z.number().int().positive(),
});
// ── İstasyon × ürün ayarı ────────────────────────────────────────────────────
export const stationProductSettingSchema = z.object({
    id: uuidSchema,
    stationId: uuidSchema,
    stationName: z.string(),
    productId: uuidSchema,
    productCode: z.string(),
    productName: z.string(),
    minStock: quantitySchema.nullable(),
    maxStock: quantitySchema.nullable(),
    shelfLocation: z.string().nullable(),
    isListed: z.boolean().describe("Ürün bu istasyonda satılıyor / sipariş önerisine giriyor mu"),
    version: z.number().int(),
    updatedAt: timestampSchema,
});
/** Toplu kaydın yanıtı: kaydedilen satırlar. */
export const stationProductSettingListSchema = z.object({ items: z.array(stationProductSettingSchema) });
export const stationProductSettingPageSchema = pageOf(stationProductSettingSchema);
export const listStationProductSettingsQuerySchema = pageQuerySchema.extend({
    stationId: uuidSchema,
    productId: uuidSchema.optional(),
    productIds: z
        .string()
        .optional()
        .describe("Virgülle ayrılmış ürün kimlikleri (toplu düzenleme için; en çok 500)")
        .transform((value) => (value ? value.split(",").map((id) => id.trim()) : undefined))
        .pipe(z.array(uuidSchema).min(1).max(500).optional()),
    search: z.string().trim().max(100).optional().describe("Ürün adı ya da kodu"),
});
const nonNegativeQuantity = quantitySchema.refine((value) => Number(value) >= 0, "Miktar negatif olamaz");
export const stationProductSettingInputSchema = z
    .object({
    productId: uuidSchema,
    minStock: nonNegativeQuantity.nullable().default(null),
    maxStock: positiveQuantitySchema.nullable().default(null),
    shelfLocation: z.string().trim().min(1).max(40).nullable().default(null),
    isListed: z.boolean().default(true),
})
    .refine((value) => value.minStock === null || value.maxStock === null || Number(value.maxStock) >= Number(value.minStock), { message: "Azami stok asgari stoktan küçük olamaz", path: ["maxStock"] });
/** Toplu kayıt (upsert): verilen ürünlerin ayarı yazılır; verilmeyenlere DOKUNULMAZ. */
export const saveStationProductSettingsSchema = z.object({
    stationId: uuidSchema,
    items: z
        .array(stationProductSettingInputSchema)
        .min(1)
        .max(2000)
        .refine((items) => new Set(items.map((item) => item.productId)).size === items.length, "Aynı ürün iki kez girilemez"),
});
// ── Depolar ──────────────────────────────────────────────────────────────────
export const warehouseSchema = z.object({
    id: uuidSchema,
    companyId: uuidSchema,
    stationId: uuidSchema,
    stationName: z.string(),
    code: z.string(),
    name: z.string(),
    kind: warehouseKindSchema,
    isDefault: z.boolean().describe("Kasa satışı istasyonun varsayılan deposundan düşer"),
    isActive: z.boolean(),
    version: z.number().int(),
    createdAt: timestampSchema,
});
export const warehouseListSchema = z.object({ items: z.array(warehouseSchema) });
export const listWarehousesQuerySchema = z.object({
    companyId: uuidSchema,
    stationId: uuidSchema.optional(),
    includeInactive: z.enum(["true", "false"]).optional(),
});
export const createWarehouseSchema = z.object({
    stationId: uuidSchema,
    code: z
        .string()
        .trim()
        .toUpperCase()
        .regex(/^[A-Z0-9][A-Z0-9_-]{0,19}$/, "Kod 1–20 karakter; büyük harf, rakam, - ve _ içerebilir"),
    name: z.string().trim().min(1, "Depo adı zorunludur").max(120),
    kind: warehouseKindSchema.default("STORE"),
    isDefault: z.boolean().default(false).describe("true → istasyonun varsayılan deposu bu olur"),
});
export const updateWarehouseSchema = patchOf(createWarehouseSchema.pick({ name: true, kind: true })).extend({
    isDefault: z.literal(true).optional().describe("Yalnızca true: varsayılanlık başka depoya AKTARILIR"),
    isActive: z.boolean().optional(),
    version: z.number().int().positive(),
});
/** İstasyonun varsayılan deposunu (yoksa `MAIN` kodla açarak) döndürür — idempotent. */
export const ensureDefaultWarehouseSchema = z.object({ stationId: uuidSchema });
//# sourceMappingURL=catalog.js.map